import React from "react";
import { Linkedin, Instagram, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { Reveal } from "./Reveal";
import { ILLO, LOGO } from "../lib/assets";
import { useEnquiry } from "../context/EnquiryContext";
import { scrollToId } from "../lib/scroll";

export default function Footer() {
  const { openEnquiry } = useEnquiry();
  return (
    <footer id="contact" className="relative bg-navy text-warm overflow-hidden" data-testid="footer">
      {/* CTA band */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-16 md:pb-20 border-b border-warm/10">
        <div className="grid md:grid-cols-2 gap-10 items-end">
          <Reveal>
            <h2 className="font-display text-4xl md:text-7xl font-light tracking-tight leading-[0.98]">
              Let&apos;s design<br />your home.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="md:text-right">
              <p className="text-warm/60 max-w-sm md:ml-auto leading-relaxed mb-6">
                Tell us about your space. We&apos;ll bring the design, the build and the delivery.
              </p>
              <button onClick={() => openEnquiry("quote", "footer")} data-testid="footer-get-in-touch-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-terracotta text-warm px-8 py-4 text-sm font-medium hover:bg-warm hover:text-navy transition-colors">
                Get In Touch
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <img src={LOGO} alt="DIDIT" className="h-11 w-auto object-contain brightness-0 invert" />
          <p className="text-warm/55 text-sm mt-6 leading-relaxed max-w-xs">
            Design · Install · Deliver. A single accountable studio for premium home interiors.
          </p>
          <div className="flex gap-3 mt-6">
            {[
              [Linkedin, "linkedin", "https://www.linkedin.com/company/blueberrydecors/"],
              [Instagram, "instagram", "https://www.instagram.com/didit_india?igsh=MWhxbmIzNnJhdmpvbQ%3D%3D&utm_source=qr"],
            ].map(([Icon, name, url]) => (
              <a key={name} href={url} target="_blank" rel="noopener noreferrer" aria-label={name} data-testid={`social-${name}`}
                className="w-10 h-10 border border-warm/20 flex items-center justify-center hover:bg-terracotta hover:border-terracotta transition-colors">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-[11px] tracking-[0.25em] uppercase text-terracotta mb-5">Navigate</p>
          <ul className="space-y-3 text-sm text-warm/70">
            {[["Services", "services"], ["Portfolio", "portfolio"], ["About", "about"]].map(([l, id]) => (
              <li key={id}><button onClick={() => scrollToId(id)} className="hover:text-warm transition-colors" data-testid={`footer-nav-${id}`}>{l}</button></li>
            ))}
            <li><a href="/how-it-works" className="hover:text-warm transition-colors" data-testid="footer-nav-how-it-works">How It Works</a></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-[11px] tracking-[0.25em] uppercase text-terracotta mb-5">Reach us</p>
          <ul className="space-y-4 text-sm text-warm/70">
            <li className="flex gap-3"><MapPin size={16} className="text-terracotta shrink-0 mt-0.5" /><span>Coming Soon — near Noida–Greater<br />Noida Expressway</span></li>
            <li className="flex gap-3"><Phone size={16} className="text-terracotta shrink-0" /><a href="tel:+919198696425" className="hover:text-warm" data-testid="footer-phone">+91 91986 96425</a></li>
            <li className="flex gap-3"><Mail size={16} className="text-terracotta shrink-0" /><a href="mailto:care@didithomes.com" className="hover:text-warm" data-testid="footer-email">care@didithomes.com</a></li>
          </ul>
        </div>

        <div className="md:col-span-2 flex md:justify-end items-end">
          <div className="w-36 h-36 bg-warm border border-warm/20 overflow-hidden">
            <img src={ILLO.reader} alt="Relax at home" className="w-full h-full object-contain" />
          </div>
        </div>
      </div>

      <div className="border-t border-warm/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-warm/45">
          <p>© {new Date().getFullYear()} DIDIT. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-warm transition-colors" data-testid="privacy-policy">Privacy Policy</a>
            <a href="#" className="hover:text-warm transition-colors" data-testid="cookie-policy">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
